/* eslint-disable react-hooks/exhaustive-deps */
import {
  useContext,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { CanvasContext } from '../upload-image/CanvasContext';
import useTooltipSequence from 'components/useTooltipSequence';
import useRegisterTooltipTarget from 'components/useRegisterTooltipTarget';
import FeatureTooltip from 'components/FeatureToolTip';
import { CROP_PANEL_TOOLTIPS } from 'constants/cropTooltips';

const CropTool = forwardRef((props, ref) => {
  const { isActive } = props;
  const {
    canvasRef,
    imageRef,
    activeTool,
    setTextItems,
    setStickerItems,
    setRedrawTrigger,
    redrawTrigger,
    pendingExitTool,
    setPendingExitTool,
    setFontScaleRatio,
    canvasScale,
    saveHistory,
  } = useContext(CanvasContext);

  const [hasCropSelection, setHasCropSelection] = useState(false);

  const start = useRef({ x: 0, y: 0 });
  const end = useRef({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const isMovingCrop = useRef(false);
  const animationFrame = useRef(null);
  const lastPointerPos = useRef({ x: 0, y: 0 });

  const bufferCanvasRef = useRef(null);
  const originalCanvasWidthRef = useRef(null);

  /* TOOLTIP FEATURE */
  const cropToolRef = useRef(null);

  const cropTooltipSequence = useTooltipSequence(CROP_PANEL_TOOLTIPS, {
    enabled: isActive && activeTool === 'crop',
    initialDelay: 500,
    stepDelay: 1800,
    visibilityThreshold: 0.8,
    visibilityDelay: 300,
  });
  useRegisterTooltipTarget(cropTooltipSequence, 'crop-tool', cropToolRef);

  /* -------------------------------------------------- */
  /* Helpers                                            */
  /* -------------------------------------------------- */

  const getPointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const updateHasCropSelection = () => {
    const w = Math.abs(end.current.x - start.current.x);
    const h = Math.abs(end.current.y - start.current.y);
    setHasCropSelection(w > 0 && h > 0);
  };

  const drawOverlay = () => {
    const canvas = canvasRef.current;
    const buffer = bufferCanvasRef.current;
    if (!canvas || !buffer) return;

    const ctx = canvas.getContext('2d');
    const x = Math.min(start.current.x, end.current.x);
    const y = Math.min(start.current.y, end.current.y);
    const w = Math.abs(end.current.x - start.current.x);
    const h = Math.abs(end.current.y - start.current.y);

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(buffer, 0, 0);

    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.fillRect(x, y, w, h);

    ctx.strokeStyle = 'black';
    ctx.lineWidth = 3;
    ctx.strokeRect(x, y, w, h);

    ctx.strokeStyle = 'white';
    ctx.lineWidth = 1;
    ctx.strokeRect(x, y, w, h);
  };

  const clearCropOverlay = () => {
    const canvas = canvasRef.current;
    const buffer = bufferCanvasRef.current;
    if (!canvas || !buffer) return;

    const ctx = canvas.getContext('2d');

    // HARD reset – prevents ghost overlays
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(buffer, 0, 0);

    start.current = { x: 0, y: 0 };
    end.current = { x: 0, y: 0 };
    isDragging.current = false;
    isMovingCrop.current = false;
    setHasCropSelection(false);
  };

  /* Expose cleanup to parent (TransformTool) */
  useImperativeHandle(ref, () => ({
    clearCropOverlay,
    applyCrop: handleCropClick,
    hasCropSelection: () => hasCropSelection,
  }));

  /* -------------------------------------------------- */
  /* Pointer handlers                                   */
  /* -------------------------------------------------- */

  const handlePointerDown = (e) => {
    if (activeTool !== 'crop') return;

    const pos = getPointerPos(e);
    lastPointerPos.current = pos;

    const x = Math.min(start.current.x, end.current.x);
    const y = Math.min(start.current.y, end.current.y);
    const w = Math.abs(end.current.x - start.current.x);
    const h = Math.abs(end.current.y - start.current.y);

    const inside = pos.x >= x && pos.x <= x + w && pos.y >= y && pos.y <= y + h;

    if (inside) {
      isMovingCrop.current = true;
    } else {
      clearCropOverlay();
      isDragging.current = true;
      start.current = pos;
      end.current = pos;
      setHasCropSelection(false);
    }

    e.preventDefault();
  };

  const handlePointerMove = (e) => {
    if (activeTool !== 'crop') return;
    const pos = getPointerPos(e);

    if (isDragging.current) {
      end.current = pos;
    } else if (isMovingCrop.current) {
      const dx = pos.x - lastPointerPos.current.x;
      const dy = pos.y - lastPointerPos.current.y;
      start.current.x += dx;
      start.current.y += dy;
      end.current.x += dx;
      end.current.y += dy;
      lastPointerPos.current = pos;
    } else return;

    updateHasCropSelection();

    if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    animationFrame.current = requestAnimationFrame(drawOverlay);
  };

  const handlePointerUp = () => {
    isDragging.current = false;
    isMovingCrop.current = false;
    updateHasCropSelection();
  };

  /* -------------------------------------------------- */
  /* Apply crop                                         */
  /* -------------------------------------------------- */

  const handleCropClick = () => {
    isDragging.current = false;
    isMovingCrop.current = false;

    const canvas = canvasRef.current;
    const buffer = bufferCanvasRef.current;
    if (!canvas || !buffer) return;

    const ctx = canvas.getContext('2d');
    const x = Math.min(start.current.x, end.current.x);
    const y = Math.min(start.current.y, end.current.y);
    const w = Math.abs(end.current.x - start.current.x);
    const h = Math.abs(end.current.y - start.current.y);

    if (!w || !h) {
      return;
    }
    // Remove the visible crop overlay before saving history,
    // otherwise undo may restore the crop rectangle too.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(buffer, 0, 0);

    saveHistory();

    const imageData = buffer.getContext('2d').getImageData(x, y, w, h);

    canvas.width = w;
    canvas.height = h;
    ctx.putImageData(imageData, 0, 0);

    const displayScale =
      canvasScale || canvas.getBoundingClientRect().width / Math.max(1, w);
    canvas.style.width = `${canvas.width * displayScale}px`;
    canvas.style.height = `${canvas.height * displayScale}px`;

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;

      const newBuffer = document.createElement('canvas');
      newBuffer.width = img.width;
      newBuffer.height = img.height;
      newBuffer.getContext('2d').drawImage(img, 0, 0);
      bufferCanvasRef.current = newBuffer;

      setTextItems((prev) =>
        prev.map((t) => ({ ...t, x: t.x - x, y: t.y - y })),
      );

      setStickerItems((items) =>
        (Array.isArray(items) ? items : []).map((sticker) => ({
          ...sticker,
          x: sticker.x - x,
          y: sticker.y - y,
        })),
      );

      if (!originalCanvasWidthRef.current)
        originalCanvasWidthRef.current = canvas.width;

      setFontScaleRatio(canvas.width / originalCanvasWidthRef.current);

      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });

      start.current = { x: 0, y: 0 };
      end.current = { x: 0, y: 0 };
      isDragging.current = false;
      isMovingCrop.current = false;
      setHasCropSelection(false);
      setRedrawTrigger((p) => p + 1);
      setPendingExitTool('crop');
    };

    img.src = canvas.toDataURL('image/png');
  };

  /* -------------------------------------------------- */
  /* Setup + listeners                                  */
  /* -------------------------------------------------- */

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const buffer = document.createElement('canvas');
    buffer.width = canvas.width;
    buffer.height = canvas.height;

    const bctx = buffer.getContext('2d');
    if (!bctx) return;

    // Copy the CURRENT visible canvas state, including any applied
    // rotate / flip / filter result already drawn onto the canvas.
    bctx.drawImage(canvas, 0, 0);

    bufferCanvasRef.current = buffer;
    setHasCropSelection(false);
  }, [canvasRef, redrawTrigger, activeTool]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || activeTool !== 'crop') return;

    canvas.addEventListener('pointerdown', handlePointerDown);
    canvas.addEventListener('pointermove', handlePointerMove);
    canvas.addEventListener('pointerup', handlePointerUp);
    canvas.addEventListener('pointerleave', handlePointerUp);

    return () => {
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerUp);
      canvas.removeEventListener('pointerleave', handlePointerUp);
    };
  }, [activeTool]);

  useEffect(() => {
    if (activeTool !== 'crop') return;

    const handleWindowPointerUp = () => {
      isDragging.current = false;
      isMovingCrop.current = false;
      updateHasCropSelection();
    };

    window.addEventListener('pointerup', handleWindowPointerUp);

    return () => {
      window.removeEventListener('pointerup', handleWindowPointerUp);
    };
  }, [activeTool]);

  useEffect(() => {
    if (pendingExitTool === 'crop') {
      setPendingExitTool(null);
    }
  }, [pendingExitTool]);

  if (!isActive) return null;

  return (
    <div className="mx-auto">
      <button
        ref={cropToolRef}
        className={`p-2 flex flex-col items-center rounded-lg uppercase ${
          hasCropSelection
            ? 'text-white bg-button'
            : 'text-white/70 bg-dk-beige cursor-not-allowed'
        }`}
        onClick={handleCropClick}
        disabled={!hasCropSelection}
      >
        Apply Crop
      </button>
      {cropTooltipSequence.activeTooltip && (
        <FeatureTooltip
          targetRef={cropTooltipSequence.activeTooltip.ref}
          open={true}
          placement={cropTooltipSequence.activeTooltip.placement || 'right'}
          align={cropTooltipSequence.activeTooltip.align || 'center'}
          title={cropTooltipSequence.activeTooltip.title}
          description={cropTooltipSequence.activeTooltip.description}
          buttonLabel="Got it"
          onClose={() =>
            cropTooltipSequence.closeTooltip(
              cropTooltipSequence.activeTooltip.id,
            )
          }
        />
      )}
    </div>
  );
});

export default CropTool;
